import type { FeatureDefinition } from '@genealogy-app/shared';
import apiClient from './client';

// ─── Tarife ──────────────────────────────────────────────────────────────────
//
// Die Liste der Funktionen kommt vom Server mit, statt sie hier zweitzuführen.
// Sonst zeigte die Konsole nach einem Server-Update Schalter, die es nicht
// mehr gibt – oder ließe neue aus.

export interface Tarif {
  id: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  /** Wie viele eigene Stammbäume ein Konto in diesem Tarif anlegen darf. */
  maxTrees: number;
  /** Speichergrenze für ALLE Bäume des Kontos zusammen. null = unbegrenzt. */
  maxStorageBytes: number | null;
  /** Personen je Stammbaum. null = unbegrenzt. */
  maxPersons: number | null;
  features: string[];
  /** Listenpreis in Cent. null = kein Preis hinterlegt. */
  priceCents: number | null;
  currency: string;
  /** 'month' | 'year' | 'once' – oder null, solange kein Preis feststeht. */
  interval: string | null;
  /** Taucht der Tarif im Shop auf? Ein interner Tarif tut das nicht. */
  imShop: boolean;
  /** Im Shop hervorgehoben dargestellt. */
  hervorgehoben: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  /** Nur in der Liste: wie viele Konten diesen Tarif haben. */
  userCount?: number;
}

export interface TarifListe {
  data: Tarif[];
  features: FeatureDefinition[];
}

export interface TarifEingabe {
  name: string;
  description?: string | null;
  maxTrees: number;
  maxStorageBytes?: number | null;
  maxPersons?: number | null;
  features?: string[];
  priceCents?: number | null;
  currency?: string;
  interval?: string | null;
  imShop?: boolean;
  hervorgehoben?: boolean;
  sortOrder?: number;
}

export async function fetchTarife(): Promise<TarifListe> {
  const { data } = await apiClient.get<TarifListe>('/plans');
  return data;
}

export async function createTarif(dto: TarifEingabe): Promise<Tarif> {
  const { data } = await apiClient.post<Tarif>('/plans', dto);
  return data;
}

export async function updateTarif(id: string, dto: Partial<TarifEingabe>): Promise<Tarif> {
  const { data } = await apiClient.patch<Tarif>(`/plans/${id}`, dto);
  return data;
}

export async function setTarifVorgabe(id: string): Promise<void> {
  await apiClient.put(`/plans/${id}/default`, {});
}

export async function deleteTarif(id: string): Promise<void> {
  await apiClient.delete(`/plans/${id}`);
}

// ─── Auswirkung eines Tarifwechsels ──────────────────────────────────────────
//
// Was der Wechsel bricht, BEVOR gespeichert wird. Verbietet nichts – der
// Betreiber soll nur sehen, was er tut.

export interface Befund {
  art: 'personen' | 'speicher' | 'baeume' | 'funktionen';
  text: string;
  /** `hart` = danach über der Grenze. `weich` = etwas verschwindet aus der Oberfläche. */
  gewicht: 'hart' | 'weich';
}

export interface Auswirkung {
  tarif: string;
  /** Betroffene Konten. Bei der Konto-Ansicht 0 oder 1. */
  konten: number;
  befunde: Befund[];
}

/** Was es für DIESES Konto bedeutet, wenn es diesen Tarif bekommt. */
export async function auswirkungFuerKonto(
  userId: string,
  planId: string,
): Promise<Auswirkung> {
  const { data } = await apiClient.get<Auswirkung>(`/plans/auswirkung/konto/${userId}`, {
    // Leer = die Vorgabe der Installation, genau wie im Dialog.
    params: planId ? { planId } : undefined,
  });
  return data;
}

/** Was geänderte Grenzen für ALLE Konten in diesem Tarif bedeuten. */
export async function auswirkungFuerTarif(
  planId: string,
  entwurf: {
    maxTrees?: number;
    maxStorageBytes?: number | null;
    maxPersons?: number | null;
    features?: string[];
  },
): Promise<Auswirkung> {
  const { data } = await apiClient.post<Auswirkung>(`/plans/${planId}/auswirkung`, entwurf);
  return data;
}
